/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { getLoggedInUserDetailsApiUrl } from '../../constants/UserAndRoles';
import { ERR_CODE_403 } from '../../constants/Common';
import { IRole } from 'src/types/UserAndRoles.type';

interface ITowerPcUserDetails {
  userName: string;
  uid: string;
  mail: string;
}

interface ISetNameAccessToken {
  givenName: string;
  sn: string;
  uid: string;
  accessToken: string;
  mail: string;
}

export interface IGenesisUserRolePermissionState {
  isFetching: boolean;
  permissions: string[];
  copPermissions: { [name: string]: string };
  isAuthorized: boolean | null;
  isEosUser: boolean;
  error: null | string;
  userId: number | null;
  userName: string;
  role: IRole[];
  accessToken: string;
  uid: string;
  mail: string;
}

const initialState: IGenesisUserRolePermissionState = {
  isFetching: false,
  permissions: [],
  copPermissions: {},
  isAuthorized: null,
  isEosUser: false,
  error: null,
  userId: null,
  userName: '',
  role: [],
  accessToken: '',
  uid: '',
  mail: '',
};

export const fetchGenesisRolePermission = createAsyncThunk(
  'genesisUserRolePermission/fetchGenesisUserRolePermission',
  async ({ accessToken, userName }: { accessToken: string; userName?: string }) =>
    axios
      .get(getLoggedInUserDetailsApiUrl(), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          TowerPcUserName: userName,
        },
      })
      .then((response: AxiosResponse) => {
        return {
          permissions:
            response?.data?.policies?.map((item: { policyName: string }) => item.policyName) || [],
          userId: response?.data?.user?.userId,
          role: response?.data?.user?.role,

          isEosUser: response?.data?.user?.isEosUser ?? false,
        };
      })
      .catch((error) => {
        if (error.request.status === 0 && error.response === undefined) {
          error.message = 'CORS';
        }
        if (error.request.status === ERR_CODE_403) {
          error.message = String(ERR_CODE_403);
        }
        throw error;
      }),
);

const genesisRolePermission = createSlice({
  name: 'genesisUserRolePermission',
  initialState,
  reducers: {
    setNameAccessToken: (
      state: IGenesisUserRolePermissionState,
      { payload }: PayloadAction<ISetNameAccessToken>,
    ) => {
      state.userName = `${payload.givenName} ${payload.sn}`;
      state.accessToken = payload.accessToken;
      state.uid = payload.uid;
      state.mail = payload.mail;
    },
    setTowerPcUserDetails: (
      state: IGenesisUserRolePermissionState,
      { payload }: PayloadAction<ITowerPcUserDetails>,
    ) => {
      state.userName = payload.userName;
      state.uid = payload.uid;
      state.mail = payload.mail;
    },
    setIsEosUser: (state: IGenesisUserRolePermissionState, { payload }: PayloadAction<boolean>) => {
      state.isEosUser = payload;
    },
    logOut: (state: IGenesisUserRolePermissionState) => {
      state.isAuthorized = null;
      state.isEosUser = false;
      state.userName = '';
      state.accessToken = '';
      state.uid = '';
      state.permissions = [];
      state.copPermissions = {};
      state.mail = '';
      state.role = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGenesisRolePermission.pending, (state: IGenesisUserRolePermissionState) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(
        fetchGenesisRolePermission.fulfilled,
        (
          state: IGenesisUserRolePermissionState,
          {
            payload,
          }: PayloadAction<{
            permissions: string[];
            userId: number;
            role: IRole[];
            isEosUser: boolean;
          }>,
        ) => {
          state.permissions = payload.permissions;
          state.userId = payload.userId;
          state.role = payload.role;
          state.isEosUser = payload.isEosUser;
          state.isFetching = false;
          state.isAuthorized = true;
          state.error = null;
        },
      )
      .addCase(
        fetchGenesisRolePermission.rejected,
        (state: IGenesisUserRolePermissionState, payload: any) => {
          state.permissions = [];
          state.userId = null;
          state.isEosUser = false;
          state.isFetching = false;
          state.isAuthorized = false;
          state.error = payload.error.message;
        },
      );
  },
});

export const getUserId = (state: { genesisUserRolePermission: IGenesisUserRolePermissionState }) =>
  state.genesisUserRolePermission.userId;

export const userRoleActions = genesisRolePermission.actions;
export default genesisRolePermission.reducer;
